import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    LayoutDashboard,
    Loader2,
    MessageSquare,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { SegmentedControl } from '../ui/SegmentedControl';
import { useAuth } from '../../context/AuthContext';
import { ReadOnlyBanner } from '../auth/WriteGuard';
import {
    askDatabaseReportQuestion,
    askReportQuestion,
    getDesignCommand,
    listDesignCommands,
    submitDesignCommand,
} from '../../services/api';


const TERMINAL_STATUSES = new Set(['success', 'failed', 'skipped_on_replay']);
const POLL_INTERVAL_MS = 2000;
export const NORM_TOPIC = (s) => (s || '').trim().toLowerCase().replace(/_/g, ' ');

/** One-line preview when a chat answer is collapsed. */
function chatAnswerPreview(text) {
    if (!text || typeof text !== 'string') return '';
    const line = text.replace(/\s+/g, ' ').trim();
    return line.length > 120 ? `${line.slice(0, 120)}…` : line;
}

/** Single card: question + collapsible answer; expand control top-left. */
function ChatAnswerCard({ question, cmd, expanded, onToggle, pending = false }) {
    const { t } = useTranslation(['report-design-chat', 'common']);
    const preview = cmd ? chatAnswerPreview(cmd.answer) : '';

    const answerBody = pending ? (
        <p className="text-xs text-gray-500 inline-flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            {t('report-design-chat:readingReport')}
        </p>
    ) : cmd?.status === 'failed' ? (
        <p className="text-sm text-red-700">{cmd.error_message || t('report-design-chat:chatFailed')}</p>
    ) : expanded ? (
        <>
            {cmd?.answer ? (
                <div className="prose prose-sm max-w-none text-gray-900">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{cmd.answer}</ReactMarkdown>
                </div>
            ) : (
                <p className="text-xs text-gray-500">{t('report-design-chat:noAnswerRecorded')}</p>
            )}
        </>
    ) : (
        <p className="text-xs text-gray-500 line-clamp-2">
            {cmd?.status === 'failed'
                ? cmd.error_message || t('report-design-chat:chatFailed')
                : preview || t('report-design-chat:noAnswerRecorded')}
        </p>
    );

    return (
        <Card padding="sm">
            <div className="flex items-start gap-2">
                <button
                    type="button"
                    onClick={pending ? undefined : onToggle}
                    disabled={pending}
                    className="shrink-0 -ml-0.5 mt-0.5 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    aria-expanded={pending || expanded}
                    aria-label={expanded ? t('report-design-chat:collapseAnswer') : t('report-design-chat:expandAnswer')}
                >
                    <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                            pending || expanded ? 'rotate-180' : ''
                        }`}
                        aria-hidden
                    />
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-primary-700 uppercase tracking-wide mb-1">
                        {t('report-design-chat:question')}
                    </p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                        {question}
                    </p>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                            {t('report-design-chat:answer')}
                        </p>
                        {answerBody}
                    </div>
                </div>
            </div>
        </Card>
    );
}


/** Build selectable dashboard options from manifest + reports list. */
export function buildDesignChatReportOptions(manifest, reportsList, databaseId, databaseName) {
    const opts = [];
    const topics = (manifest && manifest.topics) || {};
    const titleByNorm = new Map();
    const idByNorm = new Map();
    (Array.isArray(reportsList) ? reportsList : []).forEach((r) => {
        if (r && r.topic) {
            const k = NORM_TOPIC(r.topic);
            titleByNorm.set(k, r.topic);
            if (r.id != null) idByNorm.set(k, r.id);
        }
    });
    Object.keys(topics).forEach((topicKey) => {
        const entry = topics[topicKey] || {};
        if (!entry.fileBase && !entry.file_base) return;
        const title = titleByNorm.get(topicKey) || topicKey;
        opts.push({
            value: `topic:${topicKey}`,
            label: title,
            sublabel: 'Topic dashboard',
            kind: 'topic',
            topicKey,
            reportId: idByNorm.get(topicKey) || null,
        });
    });
    const overall = (manifest && manifest.overall && manifest.overall[databaseId]) || null;
    const overallStatus = overall && (overall.status || overall.codegen_status);
    if (overall && (overallStatus === 'completed' || overall.fileBase || overall.file_base)) {
        opts.push({
            value: 'overall',
            label: databaseName ? `${databaseName} — Overall` : 'Overall performance',
            sublabel: 'Database-wide dashboard',
            kind: 'overall',
            topicKey: null,
            reportId: null,
        });
    }
    return opts;
}


/** Report picker for the Report Chat page header. */
export function DesignChatReportSelector({
    reportOptions,
    selectedKey,
    onSelectedKeyChange,
    className = '',
}) {
    const { t } = useTranslation('report-design-chat');
    return (
        <div className={`flex items-center gap-2 min-w-0 ${className}`}>
            <label
                htmlFor="design-chat-report-select"
                className="text-xs font-semibold text-gray-600 uppercase tracking-wide shrink-0"
            >
                {t('report')}
            </label>
            {reportOptions.length === 0 ? (
                <span className="text-xs text-gray-500 truncate">
                    {t('noGeneratedDashboards')}
                </span>
            ) : (
                <select
                    id="design-chat-report-select"
                    value={selectedKey || ''}
                    onChange={(e) => onSelectedKeyChange(e.target.value)}
                    className="min-w-0 max-w-[280px] sm:max-w-[360px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                    {reportOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label} · {o.sublabel}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
}


/** Sidebar card showing the active report name and kind. */
export function DesignChatSelectedReportCard({ selected, className = '' }) {
    if (!selected) return null;
    return (
        <Card padding="sm" className={`border-primary-100 bg-primary-50/60 ${className}`}>
            <div className="flex items-center gap-2">
                <div className="shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center">
                    {selected.kind === 'overall' ? (
                        <LayoutDashboard className="w-4 h-4" />
                    ) : (
                        <ChevronRight className="w-4 h-4" />
                    )}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                        {selected.label}
                    </p>
                    <p className="text-[11px] text-gray-600">{selected.sublabel}</p>
                </div>
            </div>
        </Card>
    );
}


const STATUS_VARIANTS = {
    pending: 'warning',
    running: 'info',
    success: 'success',
    failed: 'error',
    skipped_on_replay: 'warning',
};


const StatusBadge = ({ status }) => {
    const variant = STATUS_VARIANTS[status] || 'default';
    const Icon =
        status === 'running'
            ? Loader2
            : status === 'success'
                ? CheckCircle2
                : status === 'failed'
                    ? AlertCircle
                    : null;
    return (
        <Badge variant={variant} className="gap-1">
            {Icon && (
                <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
            )}
            {status}
        </Badge>
    );
};


function buildModeSegments(t) {
    return [
        { id: 'design', label: t('modeDesign'), icon: LayoutDashboard },
        { id: 'chat', label: t('modeChat'), icon: MessageSquare },
    ];
}


/**
 * Embeddable chat content for the Report Chat page.
 *
 * Renders inline (no overlay / no fixed positioning). The parent supplies the
 * page chrome and lays this out next to the dashboard preview.
 *
 * The parent owns report selection (`selected`) and renders the report picker
 * in the page header.
 */
const DesignChatPanel = ({
    databaseId,
    selected,
    mode = 'design',
    onChangeMode,
    onApplied,
}) => {
    // Editors/admins may run commands & ask questions; viewers are read-only and
    // may only read existing history. The backend enforces the same rule.
    const { canWrite } = useAuth();
    const { t } = useTranslation(['report-design-chat', 'common']);
    const modeSegments = buildModeSegments(t);
    const [commands, setCommands] = useState([]);
    const [commandText, setCommandText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [activeCommandId, setActiveCommandId] = useState(null);
    const [activeCommand, setActiveCommand] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [historyError, setHistoryError] = useState(null);

    const pollTimerRef = useRef(null);
    const appliedReportedRef = useRef(new Set());
    const historyScrollRef = useRef(null);

    // Chat-mode state — backed by the same dashboard_design_commands table
    // (rows with command_type='chat'), persisted across reloads.
    const [chatCommands, setChatCommands] = useState([]);
    const [chatHistoryError, setChatHistoryError] = useState(null);
    const [chatInput, setChatInput] = useState('');
    const [chatSubmitting, setChatSubmitting] = useState(false);
    const [pendingChat, setPendingChat] = useState(null); // { question } while awaiting Claude
    const [liveAnswerId, setLiveAnswerId] = useState(null);
    const [expandedAnswerIds, setExpandedAnswerIds] = useState(() => new Set());
    const [collapsedAnswerIds, setCollapsedAnswerIds] = useState(() => new Set());
    const chatScrollRef = useRef(null);

    const isAnswerExpanded = useCallback(
        (id) => {
            if (collapsedAnswerIds.has(id)) return false;
            if (expandedAnswerIds.has(id)) return true;
            return id === liveAnswerId;
        },
        [collapsedAnswerIds, expandedAnswerIds, liveAnswerId],
    );

    const toggleAnswerExpanded = useCallback((id) => {
        if (isAnswerExpanded(id)) {
            setCollapsedAnswerIds((s) => new Set(s).add(id));
            setExpandedAnswerIds((s) => {
                const next = new Set(s);
                next.delete(id);
                return next;
            });
            setLiveAnswerId((live) => (live === id ? null : live));
        } else {
            setCollapsedAnswerIds((s) => {
                const next = new Set(s);
                next.delete(id);
                return next;
            });
            setExpandedAnswerIds((s) => new Set(s).add(id));
        }
    }, [isAnswerExpanded]);

    // Design-mode history — explicitly filter to command_type='design' so
    // chat-mode rows don't appear here.
    const refreshHistory = useCallback(async () => {
        if (!selected || !databaseId) {
            setCommands([]);
            return;
        }
        try {
            const { data } = await listDesignCommands({
                databaseId,
                dashboardKind: selected.kind,
                topicKey: selected.kind === 'topic' ? selected.topicKey : null,
                commandType: 'design',
            });
            setCommands(Array.isArray(data) ? data : []);
            setHistoryError(null);
        } catch (err) {
            setHistoryError(err?.response?.data?.detail || err?.message || 'Failed to load history');
        }
    }, [selected, databaseId]);

    const refreshChatHistory = useCallback(async () => {
        if (!selected || !databaseId) {
            setChatCommands([]);
            return [];
        }
        try {
            const { data } = await listDesignCommands({
                databaseId,
                dashboardKind: selected.kind,
                topicKey: selected.kind === 'topic' ? selected.topicKey : null,
                commandType: 'chat',
            });
            const items = Array.isArray(data) ? data : [];
            setChatCommands(items);
            setChatHistoryError(null);
            return items;
        } catch (err) {
            setChatHistoryError(err?.response?.data?.detail || err?.message || 'Failed to load history');
            return [];
        }
    }, [selected, databaseId]);

    useEffect(() => {
        refreshHistory();
    }, [refreshHistory]);

    useEffect(() => {
        if (mode === 'chat') refreshChatHistory();
    }, [refreshChatHistory, mode]);

    // Reset transient state when the selection changes.
    useEffect(() => {
        setActiveCommandId(null);
        setActiveCommand(null);
        setSubmitError(null);
        setPendingChat(null);
        setChatInput('');
        setLiveAnswerId(null);
        setExpandedAnswerIds(new Set());
        setCollapsedAnswerIds(new Set());
    }, [selected?.value]);

    const submitChatQuestion = useCallback(async (e) => {
        e?.preventDefault?.();
        const question = chatInput.trim();
        if (!question || chatSubmitting || !selected || !canWrite) return;
        if (selected.kind === 'topic' && !selected.reportId) return;
        if (selected.kind === 'overall' && !databaseId) return;

        setChatInput('');
        setChatSubmitting(true);
        setPendingChat({ question });

        try {
            if (selected.kind === 'topic') {
                await askReportQuestion(selected.reportId, { question });
            } else {
                await askDatabaseReportQuestion(databaseId, { question });
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Request failed';
            setChatHistoryError(msg);
        } finally {
            setChatSubmitting(false);
            setPendingChat(null);
            const items = await refreshChatHistory();
            const latest = items.length > 0 ? items[items.length - 1] : null;
            if (latest?.id != null) {
                setLiveAnswerId(latest.id);
                setCollapsedAnswerIds((prev) => {
                    const next = new Set(prev);
                    next.delete(latest.id);
                    return next;
                });
                setExpandedAnswerIds((prev) => {
                    const next = new Set(prev);
                    next.delete(latest.id);
                    return next;
                });
            }
        }
    }, [chatInput, chatSubmitting, selected, databaseId, refreshChatHistory, canWrite]);

    const scrollChatToBottom = useCallback(() => {
        const el = chatScrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, []);

    useLayoutEffect(() => {
        if (mode !== 'chat') return;
        scrollChatToBottom();
        const id = requestAnimationFrame(scrollChatToBottom);
        return () => cancelAnimationFrame(id);
    }, [chatCommands, pendingChat, mode, scrollChatToBottom]);

    // Poll for the in-flight command's status.
    useEffect(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (!activeCommandId) return;
        const tick = async () => {
            try {
                const { data } = await getDesignCommand(activeCommandId);
                setActiveCommand(data);
                if (data && TERMINAL_STATUSES.has(data.status)) {
                    clearInterval(pollTimerRef.current);
                    pollTimerRef.current = null;
                    refreshHistory();
                    if (data.status === 'success') {
                        setActiveCommandId(null);
                        setActiveCommand(null);
                        if (
                            typeof onApplied === 'function' &&
                            !appliedReportedRef.current.has(data.id)
                        ) {
                            appliedReportedRef.current.add(data.id);
                            onApplied(data.id);
                        }
                    }
                }
            } catch (err) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
                setSubmitError(err?.response?.data?.detail || err?.message || 'Status poll failed');
            }
        };
        tick();
        pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS);
        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [activeCommandId, refreshHistory, onApplied]);

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!selected || !canWrite) return;
        const text = commandText.trim();
        if (!text || submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const { data } = await submitDesignCommand({
                databaseId,
                dashboardKind: selected.kind,
                topicKey: selected.kind === 'topic' ? selected.topicKey : null,
                commandText: text,
            });
            setActiveCommandId(data?.id || null);
            setActiveCommand(data || null);
            setCommandText('');
            refreshHistory();
        } catch (err) {
            setSubmitError(err?.response?.data?.detail || err?.message || 'Submit failed');
        } finally {
            setSubmitting(false);
        }
    };

    const activeIsTerminal = activeCommand && TERMINAL_STATUSES.has(activeCommand.status);
    const canSubmit =
        selected &&
        mode === 'design' &&
        !submitting &&
        commandText.trim().length > 0 &&
        !(activeCommand && !activeIsTerminal);

    const historyCommands = commands.filter((cmd) => cmd.status !== 'superseded');

    const scrollHistoryToBottom = useCallback(() => {
        const el = historyScrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, []);

    useLayoutEffect(() => {
        scrollHistoryToBottom();
        const id = requestAnimationFrame(scrollHistoryToBottom);
        return () => cancelAnimationFrame(id);
    }, [historyCommands, scrollHistoryToBottom]);

    // Shown to viewers in place of the input — they can read history but not write.
    const readOnlyNotice = (
        <div className="shrink-0 px-5 pb-4 pt-4 border-t border-gray-200 bg-white">
            {onChangeMode && (
                <div className="flex justify-end mb-3">
                    <SegmentedControl
                        items={modeSegments}
                        activeId={mode}
                        onChange={onChangeMode}
                    />
                </div>
            )}
            <ReadOnlyBanner />
        </div>
    );

    return (
        <div className="h-full flex flex-col bg-white border-r border-gray-200 min-h-0">


            {mode === 'design' ? (
                <div className="flex-1 flex flex-col min-h-0">
                    {/* History — oldest at top, newest at bottom (above command) */}
                    <div
                        ref={historyScrollRef}
                        className="design-chat-history-scroll flex-1 min-h-0 overflow-y-auto px-5 pt-0 pb-4"
                    >
                        <div className="sticky top-0 z-10 -mx-5 bg-white shrink-0">
                            <div className="flex items-center justify-between gap-2 px-5 py-2 min-h-[32px]">
                                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide leading-none">
                                    {t('report-design-chat:history')}
                                </h3>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="!h-7 !min-h-7 shrink-0 px-2 text-xs leading-none"
                                    onClick={refreshHistory}
                                >
                                    {t('common:refresh')}
                                </Button>
                            </div>
                            <div className="border-b border-gray-200" aria-hidden="true" />
                        </div>
                        <div className="pt-2">
                        {historyError && (
                            <Card padding="sm" className="border-red-200 bg-red-50 mb-3">
                                <p className="text-xs text-red-700">{historyError}</p>
                            </Card>
                        )}
                        {historyCommands.length === 0 && !historyError && (
                            <p className="text-xs text-gray-500">{t('report-design-chat:noCommandsYet')}</p>
                        )}
                        <ul className="space-y-2">
                            {historyCommands.map((cmd) => (
                                <li key={cmd.id}>
                                    <Card padding="sm">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                                                {cmd.command_text}
                                            </p>
                                            <StatusBadge status={cmd.status} />
                                        </div>
                                        <p className="mt-1 text-[11px] text-gray-500">
                                            {cmd.created_at
                                                ? new Date(cmd.created_at).toLocaleString()
                                                : ''}
                                        </p>
                                        {cmd.status === 'failed' && cmd.error_message && (
                                            <details className="mt-1">
                                                <summary className="text-[11px] text-red-700 cursor-pointer">
                                                    {t('report-design-chat:errorDetails')}
                                                </summary>
                                                <pre className="mt-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1 max-h-32 overflow-auto whitespace-pre-wrap">
                                                    {cmd.error_message}
                                                </pre>
                                            </details>
                                        )}
                                    </Card>
                                </li>
                            ))}
                        </ul>
                        </div>
                    </div>

                    {/* Active command — sits directly above the command input */}
                    {activeCommand && activeCommand.status !== 'success' && (
                        <div className="px-5 pb-3 shrink-0 border-t border-gray-100">
                            <Card padding="sm">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-semibold text-gray-700">
                                        {t('report-design-chat:currentCommand')}
                                    </span>
                                    <StatusBadge status={activeCommand.status} />
                                </div>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {activeCommand.command_text}
                                </p>
                                {activeCommand.validation_attempts > 0 && (
                                    <p className="mt-1 text-[11px] text-gray-500">
                                        {t('report-design-chat:attempts', { count: activeCommand.validation_attempts })}
                                    </p>
                                )}
                                {activeCommand.status === 'failed' && activeCommand.error_message && (
                                    <pre className="mt-2 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 max-h-48 overflow-auto whitespace-pre-wrap">
                                        {activeCommand.error_message}
                                    </pre>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* Command input — pinned at bottom (viewers see a read-only notice) */}
                    {canWrite ? (
                    <form
                        onSubmit={handleSubmit}
                        className={`shrink-0 px-5 pb-4 pt-4 border-t border-gray-200 bg-white ${selected ? '' : 'pt-5'}`}
                    >
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                {t('report-design-chat:command')}
                            </label>
                            {onChangeMode && (
                                <SegmentedControl
                                    items={modeSegments}
                                    activeId={mode}
                                    onChange={onChangeMode}
                                />
                            )}
                        </div>
                        <textarea
                            value={commandText}
                            onChange={(e) => setCommandText(e.target.value)}
                            placeholder={
                                selected
                                    ? t('report-design-chat:commandPlaceholder')
                                    : t('report-design-chat:pickReportInHeader')
                            }
                            className="design-chat-command-input w-full h-[125px] rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed resize-none overflow-y-auto focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                            disabled={
                                !selected || submitting || (activeCommand && !activeIsTerminal)
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    handleSubmit(e);
                                }
                            }}
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                            {t('report-design-chat:commandHelp')}
                        </p>
                        <div className="flex justify-end mt-3">
                            <Button type="submit" disabled={!canSubmit}>
                                {submitting ? t('report-design-chat:submitting') : t('common:apply')}
                            </Button>
                        </div>
                        {submitError && (
                            <Card padding="sm" className="border-red-200 bg-red-50 mt-3">
                                <p className="text-xs text-red-700">{submitError}</p>
                            </Card>
                        )}
                    </form>
                    ) : readOnlyNotice}
                </div>
            ) : (
                <div className="flex-1 flex flex-col min-h-0">
                    <div
                        ref={chatScrollRef}
                        className="flex-1 min-h-0 overflow-y-auto px-5 pt-4 pb-4 space-y-3"
                    >
                        {!selected && (
                            <Card padding="md" className="border-dashed bg-gray-50 text-center">
                                <MessageSquare className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                                <p className="text-sm font-semibold text-gray-700">
                                    {t('report-design-chat:pickReportToChat')}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('report-design-chat:useSelectorInHeader')}
                                </p>
                            </Card>
                        )}
                        {selected && chatCommands.length === 0 && !pendingChat && (
                            <Card padding="md" className="border-dashed bg-gray-50 text-center">
                                <MessageSquare className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                                <p className="text-sm font-semibold text-gray-700">
                                    {t('report-design-chat:askAnything', { label: selected.label })}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('report-design-chat:answersFromReportOnly')}
                                </p>
                            </Card>
                        )}
                        {chatHistoryError && (
                            <Card padding="sm" className="border-red-200 bg-red-50">
                                <p className="text-xs text-red-700">{chatHistoryError}</p>
                            </Card>
                        )}

                        {chatCommands.map((cmd) => (
                            <ChatAnswerCard
                                key={cmd.id}
                                question={cmd.command_text}
                                cmd={cmd}
                                expanded={isAnswerExpanded(cmd.id)}
                                onToggle={() => toggleAnswerExpanded(cmd.id)}
                            />
                        ))}

                        {pendingChat && (
                            <ChatAnswerCard
                                question={pendingChat.question}
                                expanded
                                pending
                            />
                        )}
                    </div>

                    {canWrite ? (
                    <form
                        onSubmit={submitChatQuestion}
                        className="shrink-0 px-5 pb-4 pt-4 border-t border-gray-200 bg-white"
                    >
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                {t('report-design-chat:question')}
                            </label>
                            {onChangeMode && (
                                <SegmentedControl
                                    items={modeSegments}
                                    activeId={mode}
                                    onChange={onChangeMode}
                                />
                            )}
                        </div>
                        <textarea
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder={
                                selected
                                    ? t('report-design-chat:chatPlaceholder')
                                    : t('report-design-chat:pickReportInHeader')
                            }
                            className="design-chat-command-input w-full h-[125px] rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed resize-none overflow-y-auto focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                            disabled={!selected || chatSubmitting}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    submitChatQuestion(e);
                                }
                            }}
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                            {t('report-design-chat:chatHelp')}
                        </p>
                        <div className="flex justify-end mt-3">
                            <Button
                                type="submit"
                                disabled={
                                    !selected ||
                                    chatSubmitting ||
                                    chatInput.trim().length === 0
                                }
                            >
                                {chatSubmitting ? t('report-design-chat:asking') : t('report-design-chat:ask')}
                            </Button>
                        </div>
                    </form>
                    ) : readOnlyNotice}
                </div>
            )}
        </div>
    );
};

export default DesignChatPanel;
